import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CATEGORY_PAGE } from 'mocks/queries';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import { hasSidebarFacetFilters, mergeParentFiltersIntoCategoryPage, parseStyleCategoryPageSlug } from '../categoryPageSlugUtils';
const hasUsableAlexandriaPage = categoryPage => !!categoryPage?.pageName || hasSidebarFacetFilters(categoryPage?.filters);

/**
 * Prefer SSR Alexandria data (CategoryPageWrapper / ProductAndCategoryWrapper).
 * Client fetches only when that payload is missing (e.g. legacy app). Parent
 * filter merge matches the SSR helper for style/category pages.
 */
const useCategoryAlgoliaAlexandriaData = t0 => {
  const $ = _c(30);
  const {
    slug,
    serverCategoryData
  } = t0;
  const userLocation = useUserLocation();
  const zipCode = userLocation?.zip ?? "";
  const t1 = userLocation?.stateShortName;
  let t2;
  if ($[0] !== t1) {
    t2 = getVisitorLocationQueryVariables(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const locationVars = t2;
  const serverPage = serverCategoryData?.getCategoryPage;
  let t3;
  if ($[2] !== serverPage) {
    t3 = hasUsableAlexandriaPage(serverPage);
    $[2] = serverPage;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const needsClientFetch = !t3;
  let t4;
  if ($[4] !== locationVars || $[5] !== slug || $[6] !== zipCode) {
    t4 = {
      slug,
      zipCode,
      ...locationVars,
      limit: NUMBER_OF_PRODUCTS_TO_SHOW
    };
    $[4] = locationVars;
    $[5] = slug;
    $[6] = zipCode;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !needsClientFetch;
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = {
      variables: t4,
      skip: t5
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data: clientData
  } = useQuery(GET_CATEGORY_PAGE, t6);
  const page = (needsClientFetch ? clientData?.getCategoryPage : null) ?? serverPage;
  let t7;
  if ($[11] !== slug) {
    t7 = parseStyleCategoryPageSlug(slug);
    $[11] = slug;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const styleCategory = t7;
  let t8;
  if ($[13] !== page?.filters || $[14] !== styleCategory) {
    t8 = !!styleCategory && !hasSidebarFacetFilters(page?.filters);
    $[13] = page?.filters;
    $[14] = styleCategory;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const needsParentFilters = t8;
  const t9 = styleCategory?.categorySlug;
  let t10;
  if ($[16] !== locationVars || $[17] !== t9 || $[18] !== zipCode) {
    t10 = {
      slug: t9,
      zipCode,
      ...locationVars,
      limit: 1
    };
    $[16] = locationVars;
    $[17] = t9;
    $[18] = zipCode;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = !needsParentFilters;
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = {
      variables: t10,
      skip: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    data: parentData
  } = useQuery(GET_CATEGORY_PAGE, t12);
  const t13 = parentData?.getCategoryPage?.filters;
  let t14;
  if ($[23] !== page || $[24] !== t13) {
    t14 = mergeParentFiltersIntoCategoryPage(page, t13);
    $[23] = page;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const mergedPage = t14;
  let t15;
  if ($[26] !== mergedPage) {
    t15 = mergedPage || {};
    $[26] = mergedPage;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = {
      getCategoryPage: t15
    };
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return t16;
};
export default useCategoryAlgoliaAlexandriaData;